import { Button, Input, Label } from "@drs-ops/ui";
import { FieldError } from "@drs-ops/ui/forms";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { CrewMember } from "@/types/crewMember";

const schema = z.object({
  name: z.string().trim().min(1, "Required"),
  email: z.string().trim().email("Must be an email").or(z.literal("")).optional(),
  phone: z.string().trim().optional(),
  home_address: z.string().trim().optional(),
});
type FormValues = z.infer<typeof schema>;

export interface CrewAddEditDialogProps {
  open: boolean;
  initial?: CrewMember | undefined;
  /** QuickBooks-managed row (#168): name, email and address are read-only; phone stays editable. */
  managed?: boolean;
  onCancel: () => void;
  onSubmit: (values: FormValues) => Promise<void>;
  submitting: boolean;
}

export function CrewAddEditDialog({
  open,
  initial,
  managed = false,
  onCancel,
  onSubmit,
  submitting,
}: CrewAddEditDialogProps): React.ReactElement {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial?.name ?? "",
      email: initial?.email ?? "",
      phone: initial?.phone ?? "",
      home_address: initial?.home_address ?? "",
    },
  });

  useEffect(() => {
    form.reset({
      name: initial?.name ?? "",
      email: initial?.email ?? "",
      phone: initial?.phone ?? "",
      home_address: initial?.home_address ?? "",
    });
  }, [initial, form]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? "Edit crew member" : "Add crew member"}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={form.handleSubmit(async (v) => {
            await onSubmit(v);
          })}
        >
          {managed && <p className="text-sm text-muted-foreground">Managed in QuickBooks. Ask finance to change it.</p>}
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" readOnly={managed} {...form.register("name")} />
            {form.formState.errors.name?.message && <FieldError error={form.formState.errors.name.message} />}
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" readOnly={managed} {...form.register("email")} />
            {form.formState.errors.email?.message && <FieldError error={form.formState.errors.email.message} />}
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" {...form.register("phone")} />
          </div>
          <div>
            <Label htmlFor="home_address">Home address</Label>
            <Input id="home_address" readOnly={managed} {...form.register("home_address")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
